import { Upload } from "lucide-react";

import { ChangeEvent, DragEvent, useRef, useState } from "react";

import { cn } from "@/lib/utils";

import { fileUploadVariants } from "./constants";
import { FileUploadProps } from "./types";

import { filterAcceptedFiles, formatFileSize, getCurrentState } from "./helpers";

import { Alert, AlertDescription } from "../alert";
import { Input } from "../input";
import { Text } from "../typography";
import FileList from "./fileList";

const FileUpload = ({
    maxSize,
    maxFiles,
    accept,
    files,
    onChange,
    onFileRemove,
    onRetryUpload,
    disabled = false,
    errorMessage = "",
    acceptedFileTypesText,
}: FileUploadProps) => {
    const [dragActive, setDragActive] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    // Determine component state
    const currentState = getCurrentState(disabled, dragActive);

    // Default texts
    const maxFilesText = maxFiles > 1 ? ` • Max files ${maxFiles}` : "";
    const maxSizeText = `Max total file size ${formatFileSize(maxSize)}`;
    const helpText = (acceptedFileTypesText ? acceptedFileTypesText + " • " : "") + maxSizeText + maxFilesText;

    const handleDrag = (e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (disabled) return;

        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        }

        if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (disabled) return;

        setDragActive(false);

        const droppedFiles = Array.from(e.dataTransfer.files);
        const acceptedFiles = filterAcceptedFiles(droppedFiles, accept);

        onChange(acceptedFiles);
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        if (disabled) return;

        const target = e.target;
        onChange(target?.files ? Array.from(target.files) : []);

        // Reset input value to allow selecting the same file again
        if (inputRef.current) {
            inputRef.current.value = "";
        }
    };

    return (
        <section
            className={cn(fileUploadVariants({ state: currentState, disabled }))}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            aria-label="File upload area"
        >
            <Input
                ref={inputRef}
                type="file"
                multiple={maxFiles > 1}
                accept={accept.join(",")}
                onChange={handleChange}
                disabled={disabled}
                className="setu:hidden"
                aria-label="File upload"
            />

            <button
                type="button"
                className="setu:flex setu:flex-col setu:items-center setu:gap-4 setu:w-full setu:bg-transparent setu:border-0 setu:p-0 setu:cursor-pointer disabled:setu:cursor-not-allowed"
                onClick={() => {
                    if (!disabled) {
                        inputRef.current?.click();
                    }
                }}
                disabled={disabled}
                aria-label="Click to browse files"
            >
                <div
                    className={cn(
                        "setu:flex setu:items-center setu:justify-center setu:w-12 setu:h-12 setu:rounded-full setu:transition-colors",
                        dragActive ? "setu:bg-primary/30" : "setu:bg-muted"
                    )}
                >
                    <Upload
                        className={cn(
                            "setu:w-6 setu:h-6 setu:transition-colors",
                            dragActive ? "setu:text-primary" : "setu:text-muted-foreground"
                        )}
                        height={20}
                        width={20}
                    />
                </div>

                <div className="setu:flex setu:flex-col setu:gap-2">
                    <Text size="medium" weight="semibold">
                        Drop files here or{" "}
                        <span className="setu:text-primary setu:underline-offset-4 setu:hover:underline">
                            browse files
                        </span>
                    </Text>
                    <Text size="tiny" isMuted>
                        {helpText}
                    </Text>
                </div>
            </button>

            {!!files?.length && <FileList files={files} onFileRemove={onFileRemove} onRetryUpload={onRetryUpload} />}

            {errorMessage && (
                <Alert variant="destructive" className="setu:bg-rose-100 setu:text-left">
                    <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
            )}
        </section>
    );
};

FileUpload.displayName = "FileUpload";

export type { FileItem } from "./types";
export { FileUpload };
